import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Mail, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  requestFreeLesson,
  resendFreeLesson,
  postAnonymousFreeLesson,
  postAuthenticatedFreeLesson,
  fetchFreeLessonQuota,
} from "../lib/freeLessonApi";
import { setFreeLessonClaimed } from "../lib/freeLessonMarker";
import { generateActivity } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  getSpecificsForGradeSubject,
  getSubjectsForGrade,
  useCurriculum,
} from "../lib/curriculum";
import { track } from "../lib/analytics";
import { SkillFocusPicker } from "./SkillFocusPicker";
import ExpectationCombobox from "./ExpectationCombobox";

const GRADES = ["Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];

const FIELD =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md h-11";

// Phase 142 — required-field convention for the Simplified form:
// "(required)" in plain text after the label, no asterisks. The spec
// calls this out explicitly so a newer teacher doesn't have to decode
// a bare red asterisk.
const REQUIRED_TAG = (
  <span className="font-bold normal-case tracking-normal text-[#4a6984]/70">
    {" "}(required)
  </span>
);

/**
 * FreeLessonForm — embedded as a homepage section and as the Simplified
 * tab of /generator. Captures Skill, Grade, Subject, Expectation, and
 * optional class context.
 *
 * Phase 152.Y — Auth-aware submit: when the viewer is signed in, the
 * form behaves like every other in-app generator (button reads
 * "Generate lesson", clicking it kicks off the same `generateActivity`
 * flow as the Skill / Full tabs — no email-capture modal). The
 * email-capture flow only fires for anonymous viewers (homepage
 * marketing surface). When mounted inside the /generator Simplified
 * tab, the parent passes `onGenerate` to receive the form payload and
 * own the loading / result-display lifecycle; on the homepage embed
 * (no `onGenerate` prop), the form generates inline and navigates the
 * signed-in user to their freshly-generated lesson.
 *
 * Phase 142 — Simplified Generator Form Updates:
 *   1. Field order is now Skill → Grade → Subject → Expectation →
 *      Prompt (matches the Full generator).
 *   2. Worksheet + Exit Ticket checkboxes default ON.
 *   3. Prompt box has rich helper text.
 *   4. Required fields use the word "(required)" in plain text.
 */
export default function FreeLessonForm({ compact = false, onGenerate, loading: loadingProp = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const signedIn = !!user;
  // Lane 3B (Feb 2026) — a signed-in user is a paid teacher iff the
  // /me endpoint marked their subscription active (or they're an
  // admin). Everyone else with an account is on the FREE tier and
  // hits the lightweight `/free-lesson/authenticated` engine, capped
  // at 5 lessons per calendar month.
  const isPaid = !!(user?.subscription?.is_active || user?.is_admin);
  const isSignedInFree = signedIn && !isPaid;
  // Local loading state — only used when the form generates inline
  // (signed-in homepage path). When `onGenerate` is provided, the
  // parent owns the loading lifecycle and passes it via `loading`.
  const [localLoading, setLocalLoading] = useState(false);
  const loading = loadingProp || localLoading;
  // Lane 3B — quota badge for signed-in free tier. `quota.remaining`
  // drives the "N of 5 lessons left this month" affordance below the
  // submit button, and the cap-reached UX. Never fetched for paid
  // users (they see the normal "Generate lesson" flow untouched).
  const [quota, setQuota] = useState(null);
  const [capReached, setCapReached] = useState(false);
  useEffect(() => {
    if (!isSignedInFree) return undefined;
    let cancelled = false;
    fetchFreeLessonQuota()
      .then((q) => {
        if (cancelled) return;
        setQuota(q);
        if (q && !q.unlimited && q.remaining === 0) setCapReached(true);
      })
      .catch(() => {
        // Fail-open UX: if the quota endpoint hiccups, we still let
        // the user try to generate. The backend gate is the source
        // of truth — it will 429 if they're actually over the cap.
      });
    return () => {
      cancelled = true;
    };
  }, [isSignedInFree]);
  const [skill, setSkill] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [expectation, setExpectation] = useState(""); // code like "B1.1"
  const [context, setContext] = useState("");
  const [includeWorksheet, setIncludeWorksheet] = useState(true);
  const [includeExitTicket, setIncludeExitTicket] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const { data: curriculum } = useCurriculum();
  const subjectOptions = useMemo(() => {
    if (!curriculum || !grade) return [];
    return getSubjectsForGrade(curriculum, grade);
  }, [curriculum, grade]);
  const expectationOptions = useMemo(() => {
    if (!curriculum || !grade || !subject) return [];
    return getSpecificsForGradeSubject(curriculum, grade, subject);
  }, [curriculum, grade, subject]);
  // The selected expectation's full Ontario text — needed by the
  // backend so the LLM can ground the lesson in the verbatim curriculum
  // language. Looked up from the code each time so we never store
  // both code+text in React state (single source of truth).
  const selectedExpectation = useMemo(
    () => expectationOptions.find((e) => e.code === expectation) || null,
    [expectationOptions, expectation],
  );

  // Reset Subject when Grade changes (the canonical subject list per
  // grade can differ across grades), and reset Expectation when either
  // Grade or Subject changes (specifics are keyed by both).
  const onGradeChange = (g) => {
    setGrade(g);
    setSubject("");
    setExpectation("");
  };
  const onSubjectChange = (s) => {
    setSubject(s);
    setExpectation("");
  };

  const handleGenerate = async (e) => {
    e?.preventDefault?.();
    if (!skill || !grade || !subject) {
      toast.error("Pick a skill, grade, and subject first.");
      return;
    }
    track("free_lesson_generate_clicked", {
      grade,
      subject,
      transferable_skill: skill,
      has_expectation: !!expectation,
      include_worksheet: includeWorksheet,
      include_exit_ticket: includeExitTicket,
      has_context: !!(context || "").trim(),
      source:
        typeof window !== "undefined" && window.location?.pathname === "/free-lesson-generator"
          ? "free_lesson_generator_page"
          : "homepage",
      signed_in: signedIn,
    });

    // Lane 3A (Feb 2026) — Anonymous viewer.
    // No email, no login, no IP gate. Generate INLINE via the
    // lightweight engine and navigate to /free-lesson/:token to
    // render + print. Marker only set on a successful render below.
    if (!signedIn) {
      track("free_lesson_taste_started", {
        grade,
        subject,
        transferable_skill: skill,
        has_expectation: !!expectation,
        include_worksheet: includeWorksheet,
        include_exit_ticket: includeExitTicket,
        has_context: !!(context || "").trim(),
      });
      setLocalLoading(true);
      try {
        const payload = {
          grade,
          subject,
          transferable_skill: skill,
          context: (context || "").trim(),
          expectation_code: expectation || "",
          expectation_text: selectedExpectation?.description || "",
          include_worksheet: includeWorksheet,
          include_exit_ticket: includeExitTicket,
        };
        const res = await postAnonymousFreeLesson(payload);
        if (!res?.token) {
          throw new Error("no_token_returned");
        }
        setFreeLessonClaimed(res.token);
        track("free_lesson_taste_rendered", {
          token: res.token,
          grade,
          subject,
          transferable_skill: skill,
        });
        navigate(`/free-lesson/${encodeURIComponent(res.token)}`);
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          "We couldn't build your lesson this time. Please try again.";
        toast.error(detail);
      } finally {
        setLocalLoading(false);
      }
      return;
    }

    // Lane 3B (Feb 2026) — Signed-in FREE tier.
    // A signed-in user with no paid subscription hits the SAME
    // lightweight engine as the anonymous taste, but with their
    // user_id attached (so the row appears in /my-free-lessons) and
    // subject to the monthly cap. Server returns 429 on cap-reached
    // without calling Anthropic — we render the upgrade nudge inline.
    if (isSignedInFree) {
      track("free_tier_generate_started", {
        grade,
        subject,
        transferable_skill: skill,
        has_expectation: !!expectation,
        quota_remaining_before: quota?.remaining ?? null,
      });
      setLocalLoading(true);
      try {
        const authPayload = {
          grade,
          subject,
          transferable_skill: skill,
          context: (context || "").trim(),
          expectation_code: expectation || "",
          expectation_text: selectedExpectation?.description || "",
          include_worksheet: includeWorksheet,
          include_exit_ticket: includeExitTicket,
        };
        const res = await postAuthenticatedFreeLesson(authPayload);
        if (!res?.token) throw new Error("no_token_returned");
        // Lane 4 (Feb 2026) — opt-in save. The server no longer
        // persists the lesson on generation, so we stash it in
        // sessionStorage under a token-scoped key. FreeLessonRender
        // reads this if the server /by-token/:t GET returns 404.
        // Save-decision UI lives on the render page.
        try {
          window.sessionStorage.setItem(
            `free_lesson_session:${res.token}`,
            JSON.stringify({
              lesson: res.lesson,
              grade,
              subject,
              transferable_skill: skill,
              context: (context || "").trim(),
              expectation_code: expectation || "",
              expectation_text: selectedExpectation?.description || "",
              include_worksheet: includeWorksheet,
              include_exit_ticket: includeExitTicket,
              generated_at: new Date().toISOString(),
            }),
          );
        } catch (_e) {
          // sessionStorage quota / private-mode issues are non-fatal —
          // the render page shows an error with a "generate another"
          // fallback if it can find neither the server row nor the
          // cached copy.
        }
        setQuota((prev) => ({
          ...(prev || {}),
          used: res.quota?.used ?? (prev?.used || 0) + 1,
          remaining: res.quota?.remaining ?? Math.max(0, (prev?.remaining ?? 5) - 1),
          limit: res.quota?.limit ?? prev?.limit ?? 5,
          month_key: res.quota?.month_key ?? prev?.month_key,
          unlimited: false,
        }));
        track("free_tier_generate_succeeded", {
          token: res.token,
          quota_remaining_after: res.quota?.remaining ?? null,
        });
        navigate(`/free-lesson/${encodeURIComponent(res.token)}`);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 429 && detail?.code === "free_monthly_cap_reached") {
          // Update local UI to reflect the true cap-reached state
          // and render the upgrade nudge inline (below the form).
          setQuota({
            unlimited: false,
            used: detail.used,
            limit: detail.limit,
            remaining: 0,
            month_key: detail.month_key,
          });
          setCapReached(true);
          track("free_tier_cap_reached", {
            used: detail.used,
            limit: detail.limit,
          });
        } else {
          const msg =
            typeof detail === "string"
              ? detail
              : "We couldn't build your lesson this time. Please try again.";
          toast.error(msg);
        }
      } finally {
        setLocalLoading(false);
      }
      return;
    }

    // Signed-in PAID viewer — behave like every other in-app generator.
    // Build the payload in the shape `generateActivity` expects (same
    // shape SkillsGeneratorForm + GeneratorForm produce). When the
    // parent passed `onGenerate` (the /generator Simplified tab), hand
    // off to it and let the parent own loading + result display.
    // Otherwise (homepage embed), generate inline and navigate the
    // user to the resulting lesson.
    const payload = {
      skill_focus: skill === "Let Atomic Edu choose" ? "" : skill,
      grade,
      subject,
      expectation_code: expectation || "",
      expectation_text: selectedExpectation?.description || "",
      expectations_detail: selectedExpectation
        ? [{
            code: selectedExpectation.code,
            description: selectedExpectation.description,
            strand: selectedExpectation.strand || "",
          }]
        : [],
      prompt: (context || "").trim(),
      activity_type: "Lesson",
      output_type: "lesson",
      include_worksheet: includeWorksheet,
      include_exit_ticket: includeExitTicket,
    };

    if (typeof onGenerate === "function") {
      onGenerate(payload);
      return;
    }

    // Inline generate path (homepage + signed-in). Navigate to the new
    // activity's workspace on success.
    setLocalLoading(true);
    try {
      const activity = await generateActivity(payload);
      toast.success("Lesson ready", {
        description: `${activity?.activity_name || "Your lesson"} — auto-saved to your library.`,
      });
      if (activity?.id) {
        navigate(`/library/${activity.id}`);
      }
    } catch (err) {
      toast.error("Generation failed", {
        description: err?.message || "Please try again.",
      });
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <section
      data-testid="free-lesson-form"
      className={`ae-card rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden ${
        compact ? "" : ""
      }`}
    >
      {/* Slate header bar — matches the brand's lesson-output card.
          Phase 152.Y: marketing copy ("Try Atomic Edu" + "no account
          needed") only shows for anonymous viewers. Signed-in users
          get a neutral header that matches the in-app generator tone. */}
      <div className="bg-[#1c2d3d] text-white px-6 py-4 sm:px-8 sm:py-5">
        <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
          {isSignedInFree
            ? "Your Free Tier"
            : signedIn
              ? "Simplified Generator"
              : "Try Atomic Edu"}
        </p>
        <h2
          data-testid="free-lesson-heading"
          className="mt-1 font-black text-2xl sm:text-3xl tracking-tight leading-tight"
        >
          Build a lesson in seconds. Skills first. Always.
        </h2>
        <p className="mt-2 text-sm sm:text-base text-white/75 font-medium leading-relaxed">
          {isSignedInFree
            ? "Five free lessons a month — instant, on us. Upgrade any time for the full engine."
            : signedIn
              ? "Five quick inputs. We'll save it to your library when it's ready."
              : "Try the generator — free, no account needed. One lesson, on us."}
        </p>
      </div>

      <form
        onSubmit={handleGenerate}
        className="px-6 py-6 sm:px-8 sm:py-8 space-y-5"
      >
        {/* 1 — Skill Focus (required). The SkillFocusPicker renders its
            own label; we suppress its built-in asterisk via
            `required={false}` and bake "(required)" into the label
            text so the Simplified form's required-field convention is
            consistent across all five rows. */}
        <SkillFocusPicker
          value={skill}
          onChange={setSkill}
          testid="fl-skill"
          label="Transferable skill (required)"
          required={false}
          triggerClassName="h-11"
          showConfirmation={false}
        />
        {skill && (
          <p
            data-testid="fl-skill-confirmation"
            className="text-xs text-[#1c2d3d]/85 leading-snug -mt-2"
          >
            {skill === "Let Atomic Edu choose"
              ? "✦ Atomic Edu will pick the best-fit transferable skill for your grade + subject and name it in the lesson."
              : `✦ This lesson will weave ${skill} through the task design, look-fors, and reflection.`}
          </p>
        )}

        {/* 2 — Grade (required) */}
        <div className="space-y-1.5">
          <Label
            htmlFor="fl-grade"
            className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/80"
          >
            Grade{REQUIRED_TAG}
          </Label>
          <Select value={grade} onValueChange={onGradeChange}>
            <SelectTrigger id="fl-grade" data-testid="fl-grade" className={FIELD}>
              <SelectValue placeholder="Pick a grade" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
              {GRADES.map((g) => (
                <SelectItem
                  key={g}
                  value={g}
                  data-testid={`fl-grade-${g.replace(/\s/g, "-").toLowerCase()}`}
                  className="text-[#4a6984] focus:bg-[#f4f5f7]"
                >
                  {g}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 3 — Subject (required) — sourced from the curriculum so it
            stays in lock-step with the Expectation dropdown below. */}
        <div className="space-y-1.5">
          <Label
            htmlFor="fl-subject"
            className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/80"
          >
            Subject{REQUIRED_TAG}
          </Label>
          <Select
            value={subject}
            onValueChange={onSubjectChange}
            disabled={!grade}
          >
            <SelectTrigger id="fl-subject" data-testid="fl-subject" className={FIELD}>
              <SelectValue
                placeholder={grade ? "Pick a subject" : "Pick a grade first"}
              />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
              {subjectOptions.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`fl-subject-${s.replace(/[^\w]+/g, "-").toLowerCase()}`}
                  className="text-[#4a6984] focus:bg-[#f4f5f7]"
                >
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 4 — Expectation (optional). Searchable combobox so teachers
            in subjects with 40+ specifics can type a code ("B1.1") or
            keywords ("decompose") and jump straight to the row.
            Single-select; cleared via the X button on the trigger. */}
        <div className="space-y-1.5">
          <Label
            htmlFor="fl-expectation"
            className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/80"
          >
            Expectation
          </Label>
          <ExpectationCombobox
            options={expectationOptions}
            value={expectation}
            onChange={setExpectation}
            disabled={!subject}
            placeholder={
              !grade
                ? "Pick a grade first"
                : !subject
                ? "Pick a subject first"
                : "Pick an Ontario expectation (optional)"
            }
            testid="fl-expectation"
          />
          {selectedExpectation && (
            <p
              data-testid="fl-expectation-confirmation"
              className="text-xs text-[#1c2d3d]/85 leading-snug pt-0.5"
            >
              ✦ The lesson will explicitly target{" "}
              <span className="font-extrabold">
                {selectedExpectation.code}
              </span>{" "}
              and reflect its full Ontario wording.
            </p>
          )}
        </div>

        {/* 5 — Prompt box (optional) — Phase 142 spec: example-rich
            helper text so a newer teacher knows the full range of
            what belongs here. Single box, not split. */}
        <div className="space-y-1.5">
          <Label
            htmlFor="fl-context"
            className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/80"
          >
            Tell me what you need (optional)
          </Label>
          <Textarea
            id="fl-context"
            data-testid="fl-context"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={3}
            placeholder="e.g. Two ELL students, one IEP for reading, want a hands-on activity that emphasizes the 'compose and decompose' part of the expectation."
            className="bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md leading-relaxed text-sm"
          />
          <p
            data-testid="fl-context-helper"
            className="text-xs text-[#4a6984]/75 leading-snug"
          >
            The more you add, the better the fit — your class makeup
            (IEPs, ELLs, reading levels), the kind of activity you want,
            which expectation to emphasize, or your students&apos;
            strengths and needs.
          </p>
        </div>

        {/* Worksheet + Exit Ticket — default ON per Phase 142 spec.
            Sit below the prompt, above the Generate button. */}
        <div
          data-testid="fl-addons"
          className="rounded-md border border-[#4a6984]/15 bg-[#f4f5f7]/60 px-4 py-3 space-y-2.5"
        >
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox
              checked={includeWorksheet}
              onCheckedChange={(v) => setIncludeWorksheet(v === true)}
              data-testid="fl-include-worksheet"
              className="mt-0.5 border-[#4a6984]/40 data-[state=checked]:bg-[#1c2d3d] data-[state=checked]:border-[#1c2d3d]"
            />
            <span className="text-sm text-[#1c2d3d] font-semibold leading-snug">
              Include worksheet
              <span className="block text-[12px] text-[#4a6984]/80 font-medium">
                A short student handout aligned to the expectation + skill.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox
              checked={includeExitTicket}
              onCheckedChange={(v) => setIncludeExitTicket(v === true)}
              data-testid="fl-include-exit-ticket"
              className="mt-0.5 border-[#4a6984]/40 data-[state=checked]:bg-[#1c2d3d] data-[state=checked]:border-[#1c2d3d]"
            />
            <span className="text-sm text-[#1c2d3d] font-semibold leading-snug">
              Include exit ticket
              <span className="block text-[12px] text-[#4a6984]/80 font-medium">
                1-3 quick check-for-understanding questions for the end of the lesson.
              </span>
            </span>
          </label>
        </div>

        <div className="pt-1">
          <Button
            type="submit"
            data-testid="fl-generate"
            disabled={loading || (isSignedInFree && capReached)}
            className="ae-cta hover:ae-cta h-12 px-6 rounded-md font-extrabold w-full sm:w-auto inline-flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {signedIn ? "Generate lesson" : "Generate my free lesson"}
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
          <p className="mt-2 text-xs text-[#4a6984]/65 font-medium">
            {isSignedInFree
              ? "Instant lesson, saved to your free-lessons list."
              : signedIn
                ? "Auto-saved to your library when it's ready."
                : "Instant preview — no email required."}
          </p>

          {/* Lane 3B — signed-in free tier quota affordance. Renders
              below the submit button so it never interferes with
              existing testids or form validation. Two states:
                (a) under-cap → subtle "N of 5 left this month" line
                (b) cap-reached → calm upgrade nudge, points at /pricing
                    for the 7-day trial */}
          {isSignedInFree && quota && !quota.unlimited && !capReached ? (
            <p
              data-testid="fl-quota-remaining"
              className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#4a6984]/75"
            >
              {quota.remaining} of {quota.limit} free lessons left this month
            </p>
          ) : null}
          {isSignedInFree && capReached ? (
            <div
              data-testid="fl-cap-reached"
              className="mt-4 rounded-xl border border-[#e6a63a]/40 bg-[#fdf7e6] p-4 sm:p-5"
            >
              <p className="text-sm font-extrabold text-[#7a5a12] leading-snug">
                You&apos;ve used your 5 free lessons this month.
              </p>
              <p className="mt-1 text-xs text-[#7a5a12]/85 font-medium leading-relaxed">
                Your free allowance resets on the 1st. Start a 7-day free
                trial to keep generating today — full engine, slide decks,
                exports, planner, and every subject unlocked.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/pricing"
                  data-testid="fl-cap-cta-trial"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
                >
                  Start 7-day trial
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/my-free-lessons"
                  data-testid="fl-cap-cta-mine"
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#4a6984]/25 bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d] hover:bg-[#f4f5f7]"
                >
                  View my free lessons
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </form>

      {modalOpen ? (
        <EmailCaptureModal
          grade={grade}
          subject={subject}
          skill={skill}
          expectationCode={selectedExpectation?.code || ""}
          expectationText={selectedExpectation?.description || ""}
          includeWorksheet={includeWorksheet}
          includeExitTicket={includeExitTicket}
          context={context}
          onClose={() => setModalOpen(false)}
        />
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Modal — captures email + handles the three submit states.          */
/* ------------------------------------------------------------------ */

const EmailCaptureModal = ({
  grade,
  subject,
  skill,
  expectationCode,
  expectationText,
  includeWorksheet,
  includeExitTicket,
  context,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // 'form' | 'sent' | 'already_claimed'
  const [step, setStep] = useState("form");
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned) return;
    setSubmitting(true);
    try {
      const res = await requestFreeLesson({
        email: cleaned,
        grade,
        subject,
        transferable_skill: skill,
        expectation_code: expectationCode || null,
        expectation_text: expectationText || null,
        include_worksheet: !!includeWorksheet,
        include_exit_ticket: !!includeExitTicket,
        context: context || "",
      });
      if (res?.status === "already_claimed") {
        track("free_lesson_already_claimed", {
          grade,
          subject,
          transferable_skill: skill,
        });
        setStep("already_claimed");
      } else {
        track("free_lesson_email_submitted", {
          grade,
          subject,
          transferable_skill: skill,
        });
        setStep("sent");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error("Couldn't send your lesson", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) return;
    setResending(true);
    try {
      await resendFreeLesson(email.trim().toLowerCase());
      toast.success("Magic link re-sent — check your inbox.");
    } catch {
      toast.error("Couldn't resend just now. Try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="fl-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1c2d3d]/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          data-testid="fl-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full grid place-items-center text-white/80 hover:text-white hover:bg-white/10"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="bg-[#1c2d3d] text-white px-6 py-5">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
            Almost there
          </p>
          <h3 className="mt-1 font-black text-2xl tracking-tight">
            {step === "sent"
              ? "Check your inbox"
              : step === "already_claimed"
              ? "You've already claimed your free lesson."
              : "Almost there"}
          </h3>
        </div>
        <div className="px-6 py-6 space-y-4">
          {step === "form" && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="italic text-sm text-[#4a6984]/85 leading-relaxed">
                Enter your email and we&apos;ll send your lesson straight to
                you. No password. No credit card.
              </p>
              <div className="space-y-1.5">
                <Label
                  htmlFor="fl-email"
                  className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/80"
                >
                  Email address
                </Label>
                <Input
                  id="fl-email"
                  data-testid="fl-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.ca"
                  className="bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md h-11"
                />
              </div>
              <Button
                type="submit"
                data-testid="fl-send"
                disabled={submitting || !email.trim()}
                className="ae-cta hover:ae-cta h-12 w-full rounded-md font-extrabold inline-flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" /> Send my lesson <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
              <p className="text-xs text-[#4a6984]/65 text-center">
                Already have an account?{" "}
                <Link
                  to="/pricing"
                  data-testid="fl-signin-link"
                  className="font-bold text-[#1c2d3d] hover:underline"
                >
                  Sign in →
                </Link>
              </p>
            </form>
          )}

          {step === "sent" && (
            <div className="space-y-4">
              <p className="text-[#4a6984] leading-relaxed">
                We&apos;ve sent your lesson to{" "}
                <span className="font-extrabold text-[#1c2d3d]">{email}</span>.
                Click the link in the email to open it — it&apos;s good for 24
                hours.
              </p>
              <p className="text-xs text-[#4a6984]/70 italic">
                Didn&apos;t get it? Check your spam folder or{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  data-testid="fl-resend"
                  className="font-extrabold text-[#1c2d3d] hover:underline disabled:opacity-50"
                >
                  {resending ? "resending…" : "resend →"}
                </button>
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                data-testid="fl-modal-done"
                className="w-full h-11 rounded-md font-bold border-[#4a6984]/30 text-[#4a6984] hover:bg-[#f4f5f7]"
              >
                Got it
              </Button>
            </div>
          )}

          {step === "already_claimed" && (
            <div className="space-y-4">
              <p className="text-[#4a6984] leading-relaxed">
                Ready to unlock unlimited planning? Start your 7-day free trial
                — no credit card needed.
              </p>
              <Button
                asChild
                data-testid="fl-see-plans"
                className="ae-cta hover:ae-cta h-12 w-full rounded-md font-extrabold inline-flex items-center justify-center gap-2"
              >
                <Link to="/pricing">
                  See plans <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
